import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import useReusableOCR from './hooks/useReusableOCR';
import ReusableFileUploadArea from './components/ReusableFileUploadArea';
import ReusableManualEntryForm from './components/ReusableManualEntryForm';
import ReusableOCRFailureModal from './components/ReusableOCRFailureModal';

const ReusableOCRFlow = ({
    // OCR Configuration
    ocrService,
    title = "Add Details",
    
    // OCR Service Options
    consent = 'Y',
    allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'],
    maxSize = 5 * 1024 * 1024,
    extractorFunction,
    
    // Upload Configuration
    uploadTitle = "Upload Document",
    processingText = "Processing Document...",
    acceptedFileTypes = ['.jpg', '.jpeg', '.png', '.pdf'],
    
    // Form Configuration
    requirePhone = true,
    requireEmail = false,
    requireRelation = false,
    relations = [],
    minAge = 18,
    maxAge = 60,
    context = 'beneficiary',
    
    // Document Type Selection
    showDocumentTypeSelection = false,
    selectedDocumentType = 'AADHAAR',
    onDocumentTypeChange,
    relationData = null, // Pass the relation data to determine chip visibility
    
    // UI Configuration
    showConfirmModal = true,
    confirmModalTitle = "Confirm Details",
    saveButtonText = "Save & Continue",
    editManuallyText = "No, Fill Manually",
    confirmText = "Yes",
    confirmationCheckboxText = "Details provided herein are true and I have obtained all relevant organisational consents for the purposes.",
    
    // Manual Form Configuration
    manualFormTitle = "Enter Details Manually",
    showCancelButton = true,
    cancelButtonText = "Back to Upload",
    
    // Failure Modal Configuration
    failureModalTitle = "Document Invalid",
    tryAgainText = "Try Again",
    enterManuallyText = "Enter Manually",
    
    // Additional Customization
    additionalFields = [],
    customValidation,
    
    // Callbacks
    onSave,
    onCancel,
    onSuccess,
    onError,
    onFieldChange,
    onStepChange,
    
    // Initial Data
    initialData = {},
    
    // Advanced Options
    enableToast = true,
    autoOpenFailureModal = true,
    skipConfirmModal = false,
    
    // Reset Control
    resetTrigger = 0
}) => {
    const [currentStep, setCurrentStep] = useState('upload'); // 'upload', 'manual'
    const [phoneNumber, setPhoneNumber] = useState(initialData.phone_number || '');
    const [relation, setRelation] = useState(
        initialData.relation || 
        (relations && relations.length > 0 && requireRelation ? relations[0].value : '')
    );
    const [ocrFailureModalOpen, setOcrFailureModalOpen] = useState(false);

    // Set default relation when relations prop changes and no current relation is set
    useEffect(() => {
        if (requireRelation && relations && relations.length > 0 && !relation) {
            const defaultRelation = relations[0].value || relations[0];
            setRelation(defaultRelation);
            if (onFieldChange) {
                onFieldChange('relation', defaultRelation);
            }
        }
    }, [relations, requireRelation, relation, onFieldChange]);

    // Initialize OCR hook
    const {
        isLoading,
        error,
        extractedData,
        uploadedFile,
        handleFileUpload,
        resetOCR
    } = useReusableOCR({
        ocrService,
        consent,
        allowedTypes,
        maxSize,
        extractorFunction,
        enableToast,
        onSuccess: (data, response) => {
            if (onSuccess) {
                onSuccess(data, response);
            }
        },
        onError: (errorMessage, response) => {
            if (autoOpenFailureModal) {
                setOcrFailureModalOpen(true);
            }
            if (onError) {
                onError(errorMessage, response);
            }
        }
    });

    // Reset OCR flow when resetTrigger changes
    useEffect(() => {
        if (resetTrigger > 0) {
            setCurrentStep('upload');
            setOcrFailureModalOpen(false);
            resetOCR();
            setPhoneNumber(initialData.phone_number || '');
            setRelation(
                initialData.relation || 
                (relations && relations.length > 0 && requireRelation ? relations[0].value : '')
            );
        }
    }, [resetTrigger, initialData.phone_number, initialData.relation, relations, requireRelation, resetOCR]);

    // Handle file upload with OCR processing
    const handleFileUploadWithOCRHandling = async (file) => {
        const success = await handleFileUpload(file);
        if (!success && autoOpenFailureModal) {
            setOcrFailureModalOpen(true);
        }
        return success;
    };

    // Handle step changes
    const changeStep = (newStep) => {
        setCurrentStep(newStep);
        if (onStepChange) {
            onStepChange(newStep);
        }
    };

    // OCR Failure Modal Handlers
    const handleTryAgain = () => {
        setOcrFailureModalOpen(false);
        changeStep('upload');
        resetOCR();
    };

    const handleEnterManually = () => {
        setOcrFailureModalOpen(false);
        changeStep('manual');
    };

    // Navigation handlers
    const handleBackToUpload = () => {
        changeStep('upload');
        resetOCR();
        setPhoneNumber(initialData.phone_number || '');
        setRelation(initialData.relation || '');
    };

    const handleEditManually = () => {
        changeStep('manual');
    };

    // Data confirmation handler
    const handleConfirmExtractedData = async (formData = {}) => {
        const combinedData = {
            ...extractedData,
            phone_number: phoneNumber,
            relation: relation,
            ocr_verified: currentStep === 'upload',
            ...formData
        };

        if (onSave) {
            await onSave(combinedData);
        }
    };

    // Manual form save handler
    const handleManualSave = async (formData) => {
        const combinedData = {
            ...formData,
            phone_number: formData.phone_number || phoneNumber,
            relation: formData.relation || relation,
            ocr_verified: false
        };

        if (onSave) {
            await onSave(combinedData);
        }
    };

    // Handle field changes
    const handleFieldChange = (fieldName, value) => {
        if (fieldName === 'phone_number') {
            setPhoneNumber(value);
        } else if (fieldName === 'relation') {
            setRelation(value);
        }

        if (onFieldChange) {
            onFieldChange(fieldName, value);
        }
    };

    // Handle cancel
    const handleCancel = () => {
        if (onCancel) {
            onCancel();
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: currentStep === 'manual' ? 1 : 0 }}>
            {title && (
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {title}
                </Typography>
            )}

            {/* Upload Step */}
            {currentStep === 'upload' && (
                <ReusableFileUploadArea
                    onFileUpload={handleFileUploadWithOCRHandling}
                    onFileDelete={resetOCR}
                    isLoading={isLoading}
                    error={error}
                    uploadedFile={uploadedFile}
                    extractedData={extractedData}
                    phoneNumber={phoneNumber}
                    onPhoneChange={setPhoneNumber}
                    relation={relation}
                    onRelationChange={setRelation}
                    relations={relations}
                    uploadTitle={uploadTitle}
                    processingText={processingText}
                    acceptedFileTypes={acceptedFileTypes}
                    requirePhone={requirePhone}
                    requireRelation={requireRelation}
                    minAge={minAge}
                    maxAge={maxAge}
                    context={context}
                    onConfirm={handleConfirmExtractedData}
                    onEditManually={handleEditManually}
                    showConfirmModal={showConfirmModal && !skipConfirmModal}
                    confirmModalTitle={confirmModalTitle}
                    saveButtonText={saveButtonText}
                    editManuallyText={editManuallyText}
                    confirmText={confirmText}
                    confirmationCheckboxText={confirmationCheckboxText}
                    additionalFields={additionalFields}
                    customValidation={customValidation}
                    onFieldChange={handleFieldChange}
                    showDocumentTypeSelection={showDocumentTypeSelection}
                    selectedDocumentType={selectedDocumentType}
                    onDocumentTypeChange={onDocumentTypeChange}
                    relationData={relationData}
                />
            )}

            {/* Manual Entry Step */}
            {currentStep === 'manual' && (
                <ReusableManualEntryForm
                    onSave={handleManualSave}
                    onCancel={showCancelButton ? handleBackToUpload : handleCancel}
                    initialData={{
                        ...initialData,
                        ...extractedData,
                        phone_number: phoneNumber,
                        relation: relation
                    }}
                    title={manualFormTitle}
                    requirePhone={requirePhone}
                    requireEmail={requireEmail}
                    requireRelation={requireRelation}
                    relations={relations}
                    minAge={minAge}
                    maxAge={maxAge}
                    context={context}
                    showConfirmation={true}
                    confirmationText={confirmationCheckboxText}
                    saveButtonText={saveButtonText}
                    cancelButtonText={cancelButtonText}
                    showCancelButton={showCancelButton}
                    additionalFields={additionalFields}
                    customValidation={customValidation}
                    onFieldChange={handleFieldChange}
                />
            )}

            {/* OCR Failure Modal */}
            <ReusableOCRFailureModal
                open={ocrFailureModalOpen}
                onClose={() => {
                    setOcrFailureModalOpen(false);
                    resetOCR();
                }}
                onTryAgain={handleTryAgain}
                onEnterManually={handleEnterManually}
                errorMessage={error}
                title={failureModalTitle}
                tryAgainText={tryAgainText}
                enterManuallyText={enterManuallyText}
            />
        </Box>
    );
};

export default ReusableOCRFlow;